"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useRef, useState } from "react";

import { useGetFreeAssessmentModal } from "@/components/campaign/GetFreeAssessmentModalContext";

export interface WBannerSectionProps {
  logoUrl?: string;
  logoAlt?: string;
  logoLink?: string;
  title1?: string;
  title1Color?: string;
  title2?: string;
  title2Color?: string;
  description?: string;
  descriptionColor?: string;
  buttonName?: string;
  buttonLink?: string;
  imageUrl?: string;
  imageAlt?: string;
  sectionBackgroundColor1?: string;
  sectionBackgroundColor2?: string;
  sectionBackgroundDirection?: string;
  mobileCardBackgroundColor1?: string;
  mobileCardBackgroundColor2?: string;
  mobileCardBackgroundDirection?: string;
  slug?: string;
  attachTop?: boolean;
  /** Campaign-style primary CTA: black fill, white label */
  useSolidBlackCta?: boolean;
}

const defaultBg = "#f5f3ff";
const defaultCardBg = "#ffffff";
const defaultTextColor = "#0a0a0a";

export default function WBannerSection({
  logoUrl,
  logoAlt,
  logoLink,
  title1,
  title1Color = defaultTextColor,
  title2,
  title2Color = "#4c1d95",
  description,
  descriptionColor = "#4b5563",
  buttonName,
  buttonLink,
  imageUrl,
  imageAlt,
  sectionBackgroundColor1 = defaultBg,
  sectionBackgroundColor2,
  sectionBackgroundDirection = "to right",
  mobileCardBackgroundColor1 = defaultCardBg,
  mobileCardBackgroundColor2,
  mobileCardBackgroundDirection = "to right",
  attachTop = false,
  useSolidBlackCta = false,
}: WBannerSectionProps) {
  const [glitchKey, setGlitchKey] = useState(0);
  const lastGlitchAt = useRef(0);
  const GLITCH_THROTTLE_MS = 180;

  const rawLink = (buttonLink ?? "").trim();
  const hashIndex = rawLink.indexOf("#");
  const hashId =
    hashIndex >= 0 && hashIndex < rawLink.length - 1
      ? rawLink.slice(hashIndex + 1).split("?")[0].trim() || null
      : null;
  const isSamePageAnchor = Boolean(hashId);

  const modalContext = useGetFreeAssessmentModal();

  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!hashId) return;
    e.preventDefault();
    if (hashId === "get-free-assessment" && modalContext?.openModal) {
      modalContext.openModal();
      return;
    }
    const el = document.getElementById(hashId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${hashId}`);
      return;
    }
    window.history.replaceState(null, "", `#${hashId}`);
    const deadline = Date.now() + 8000;
    const interval = setInterval(() => {
      const target = document.getElementById(hashId);
      if (target) {
        clearInterval(interval);
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (Date.now() > deadline) clearInterval(interval);
    }, 150);
  };

  const handleSectionMouseMove = () => {
    const now = Date.now();
    if (now - lastGlitchAt.current < GLITCH_THROTTLE_MS) return;
    lastGlitchAt.current = now;
    setGlitchKey((k) => k + 1);
  };

  const color1 = sectionBackgroundColor1 || defaultBg;
  const color2 = sectionBackgroundColor2;
  const direction = sectionBackgroundDirection || "to right";
  const sectionBgStyle =
    color2 && color2 !== color1
      ? { background: `linear-gradient(${direction}, ${color1}, ${color2})` }
      : { backgroundColor: color1 };
  const cardColor1 = mobileCardBackgroundColor1 || defaultCardBg;
  const cardColor2 = mobileCardBackgroundColor2;
  const cardDirection = mobileCardBackgroundDirection || "to right";
  const cardBgStyle =
    cardColor2 && cardColor2 !== cardColor1
      ? { background: `linear-gradient(${cardDirection}, ${cardColor1}, ${cardColor2})` }
      : { backgroundColor: cardColor1 };

  const hasContent =
    logoUrl ||
    title1 ||
    title2 ||
    description ||
    (buttonName && buttonLink) ||
    imageUrl;
  if (!hasContent) return null;

  const desktopCtaClass = useSolidBlackCta
    ? "inline-block rounded-2xl border border-black bg-black px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-neutral-900"
    : "inline-block rounded-2xl border border-[#F7E9AE] bg-transparent px-6 py-3 text-sm font-semibold text-[#F7E9AE] transition-colors hover:opacity-90";
  const mobileCtaClass = useSolidBlackCta
    ? "mt-4 inline-block rounded-2xl border border-black bg-black px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-neutral-900"
    : "mt-4 inline-block rounded-2xl border border-[#F7E9AE] bg-transparent px-5 py-3 text-base font-semibold text-[#F7E9AE] transition-colors hover:opacity-90";

  const logoImage = logoUrl ? (
    <Image
      src={logoUrl}
      alt={logoAlt || "Logo"}
      width={160}
      height={64}
      className="h-12 w-auto object-contain md:h-14"
      unoptimized={
        logoUrl.startsWith("http") && !logoUrl.includes("trichos")
      }
    />
  ) : null;

  const logoBlock =
    logoImage &&
    (logoLink ? (
      <Link
        href={logoLink}
        className="inline-block rounded focus:outline-none focus:ring-2 focus:ring-offset-2"
      >
        {logoImage}
      </Link>
    ) : (
      <span className="inline-block">{logoImage}</span>
    ));

  const renderGlitchTitle2 = (
    text: string,
    color: string,
    className: string,
    enableGlitch: boolean,
  ) => {
    if (!enableGlitch) {
      return <span className={className} style={{ color }}>{text}</span>;
    }
    return (
      <span className="relative inline-block overflow-hidden pointer-events-none">
        <span className={className} style={{ color }}>
          {text}
        </span>
        <span
          key={glitchKey}
          className={`absolute left-0 top-0 whitespace-nowrap ${className} pointer-events-none`}
          style={{ color }}
          aria-hidden
        >
          <span
            className="block animate-[wbanner-glitch_0.2s_ease-in-out]"
            style={{
              textShadow:
                "2px 0 rgba(239,68,68,0.6), -2px 0 rgba(34,211,238,0.6)",
            }}
          >
            {text}
          </span>
        </span>
        <span
          key={`${glitchKey}-b`}
          className={`absolute left-0 top-0 whitespace-nowrap ${className} pointer-events-none`}
          style={{ color }}
          aria-hidden
        >
          <span
            className="block animate-[wbanner-glitch_0.2s_ease-in-out_0.04s]"
            style={{
              textShadow:
                "-2px 0 rgba(239,68,68,0.5), 2px 0 rgba(34,211,238,0.5)",
            }}
          >
            {text}
          </span>
        </span>
      </span>
    );
  };

  return (
    <section
      className={`relative w-full min-h-[80vh] overflow-visible ${attachTop ? "pb-8 pt-6 md:pt-0 md:pb-0" : "py-8 md:py-12 md:pb-0"}`}
      style={sectionBgStyle}
      onMouseMove={handleSectionMouseMove}
    >
      {/* Desktop: image ~70% of section, behind left content (z-0) */}
      {imageUrl && (
        <div className="absolute right-0 bottom-0 z-0 hidden md:block">
          <Image
            src={imageUrl}
            alt={imageAlt || "Banner"}
            width={720}
            height={600}
            className="h-[72vh] w-[70vw] max-w-[900px] object-contain object-right-bottom"
            unoptimized={
              imageUrl.startsWith("http") && !imageUrl.includes("trichos")
            }
            sizes="(min-width: 1280px) 900px, 70vw"
          />
        </div>
      )}
      <div className="relative z-10 mx-auto flex min-h-[80vh] max-w-6xl flex-col justify-center px-4 pt-2 md:pt-0 md:pb-0 w-full">
        {logoBlock && (
          <div className="mb-4 md:mb-6">{logoBlock}</div>
        )}
        <div className="relative hidden md:block md:min-h-[80vh]">
          <div className="flex min-h-[80vh] max-w-[52%] flex-col justify-center gap-3 pb-2 pr-4 lg:gap-4 lg:pr-8 md:pb-8">
            {title1 && (
              <h2
                className="max-w-xl font-bold leading-[1.1] text-[clamp(2rem,4.5vw,5rem)]"
                style={{ color: title1Color }}
              >
                {title1}
              </h2>
            )}
            {title2 && (
              <h3
                className="font-black leading-[1.1] text-[clamp(2rem,4.5vw,5rem)]"
                style={{ color: title2Color }}
              >
                {renderGlitchTitle2(
                  title2,
                  title2Color,
                  "font-black leading-[1.1] text-[clamp(2rem,4.5vw,5rem)]",
                  true,
                )}
              </h3>
            )}
            {description && (
              <p
                className="max-w-xl leading-snug text-[clamp(1rem,1.1vw,1.125rem)]"
                style={{ color: descriptionColor }}
              >
                {description}
              </p>
            )}
            {buttonName && buttonLink && (
              <div className="pt-2">
                {isSamePageAnchor ? (
                  <a
                    href={`#${hashId}`}
                    onClick={handleHashClick}
                    className={desktopCtaClass}
                  >
                    {buttonName}
                  </a>
                ) : (
                  <Link href={buttonLink} className={desktopCtaClass}>
                    {buttonName}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile: full viewport width and centered so image/box are truly centered */}
        <div
          className="relative md:hidden flex flex-col items-center leading-[0] min-w-0"
          style={{
            width: "100vw",
            marginLeft: "calc(-50vw + 50%)",
            marginRight: "calc(-50vw + 50%)",
          }}
        >
          {/* Image behind the box (lower z-index), centered */}
          {imageUrl && (
            <div className="relative z-0 block w-[70%] max-w-[320px] shrink-0 overflow-hidden rounded-t-2xl leading-[0] m-0 p-0" style={{ marginLeft: "auto", marginRight: "auto" }}>
              <div className="relative block aspect-[4/3] w-full leading-[0] m-0 p-0">
                <Image
                  src={imageUrl}
                  alt={imageAlt || "Banner"}
                  fill
                  className="block object-cover object-top m-0 p-0"
                  sizes="300px"
                  unoptimized={
                    imageUrl.startsWith("http") &&
                    !imageUrl.includes("trichos")
                  }
                />
              </div>
            </div>
          )}
          {/* Box: in front of image (higher z-index), centered */}
          <div
            className="relative z-10 -mt-px w-[85%] max-w-[320px] shrink-0 overflow-hidden rounded-2xl shadow-lg"
            style={{ ...cardBgStyle, marginLeft: "auto", marginRight: "auto" }}
          >
            <div className="flex flex-col items-center justify-center px-8 py-6 text-center">
              {title1 && (
                <h2
                  className="max-w-sm text-[1.875rem] font-bold leading-[1.1] md:text-3xl"
                  style={{ color: title1Color }}
                >
                  {title1}
                </h2>
              )}
              {title2 && (
                <h3
                  className="mt-2 text-[1.875rem] font-black leading-[1.1] md:text-2xl"
                  style={{ color: title2Color }}
                >
                  {renderGlitchTitle2(
                    title2,
                    title2Color,
                    "text-[1.875rem] font-black leading-[1.1] md:text-2xl",
                    false,
                  )}
                </h3>
              )}
              {description && (
                <p
                  className="mt-3 max-w-md text-base leading-snug"
                  style={{ color: descriptionColor }}
                >
                  {description}
                </p>
              )}
              {buttonName && buttonLink && (
                isSamePageAnchor ? (
                  <a
                    href={`#${hashId}`}
                    onClick={handleHashClick}
                    className={mobileCtaClass}
                  >
                    {buttonName}
                  </a>
                ) : (
                  <Link href={buttonLink} className={mobileCtaClass}>
                    {buttonName}
                  </Link>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
